import { dom } from "@omegagrid/core";
import { CellModel, CellSource, ColumnSource, CommentItem, RequestFilters, RowSource } from "@omegagrid/grid";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions } from "@reporting/rp-app-host";
import { JobSchedule } from "@reporting/rp-types/server";
import { JobScheduleDetail } from "../components";

const PAGE_SIZE = 500;
const COL_WIDTH = 50;
const MIN_SCHEDULE_WIDTH = 5;
const QUARTER_MINUTES = 15;
const MINUTES_IN_HOUR = 60;

type ColProps = {
	h: number; // hour
	q: number; // quarter
};

export class JobScheduleGridAdapter extends GridEditorAdapter {

	private _columns?: ColumnSource[];

	constructor(
		private idServer?: number,
		private weekDay: number = 1,
	) { super() }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;

		this._columns = [{
			data: 'job_name',
			t: 't',
			w: 250,
			header: msg('Job'),
		}];

		for (let h = 0; h < 24; h++) {
			for (let q = 0; q < 4; q++) {
				this._columns.push({
					data: 'col_' + h + '_' + q,
					t: 't',
					w: COL_WIDTH,
					header: String(h).padStart(2, '0') + ':' + String(q * 15).padStart(2, '0'),
					props: { h, q } as ColProps
				});
			}
		}

		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const rows: RowSource[] = [];
		const schedules = await rp.ms('server').get<JobSchedule[]>(`/jobSchedule/${this.weekDay}/${this.idServer}`, this.createRequestParams(filters, {
			fields: ['job_name', 'schedule_name', 'time', 'run_duration'],
			count: PAGE_SIZE
		}));

		// group by job_name so one row contains multiple schedules
		const jobs = new Map<string, { job_name: string; [key: string]: any }>();
		for (const item of schedules) {
			let data = jobs.get(item.job_name);
			if (!data) {
				data = { job_name: item.job_name };
				jobs.set(item.job_name, data);
			}
			this.fillQuarters(data, item);
		}

		for (const data of jobs.values()) {
			rows.push({ data });
		}
		return rows;
	}

	/**
	 * Fills the quarter cells for a job schedule item.
	 * @param data row data to fill (grouped by job_id)
	 * @param item job schedule item (unique)
	 * @returns
	 */
	private fillQuarters(data: { [key: string]: Partial<CellSource> }, item: JobSchedule) {
		const m = (item.time ?? '').toString().match(/^(\d{1,2}:\d{2})/);
		const time = m ? this.parseTime(m[1]) : null;
		if (!time) return;

		const startHour = time.h;
		const startMinute = time.m;
		const startQuarter = Math.floor(startMinute / QUARTER_MINUTES);

		const duration = Math.max(1, Math.round((item.run_duration || 0) / 60)); // run_duration in seconds -> minutes
		const minutesSinceStart = startHour * MINUTES_IN_HOUR + startMinute;
		const minutesUntilEnd = MINUTES_IN_HOUR * 24 - minutesSinceStart;
		const minutesFromQuarterStart = startMinute % QUARTER_MINUTES; // offset in minutes from start of quarter
		const offsetPx = minutesFromQuarterStart / QUARTER_MINUTES * COL_WIDTH;

		if (duration <= minutesUntilEnd) {
			// fits within same day — single cell or multiple cells
			// We calculate how many quarters we need from the start of the event to its end
			const totalMinutesNeeded = minutesFromQuarterStart + duration;
			const quarters = Math.ceil(totalMinutesNeeded / QUARTER_MINUTES);
			const widthPx = Math.max(this.minutesToPx(duration), MIN_SCHEDULE_WIDTH);
			data[this.getColKey(startHour, startQuarter)] = this.createQuarterCell(widthPx, quarters, offsetPx, item);
		} else {
			// spills over midnight — split to end of day and beginning of day
			// first chunk at startHour/startQuarter (to end of day)
			const firstPartMinutes = minutesUntilEnd;
			const totalMinutesInFirstPart = minutesFromQuarterStart + firstPartMinutes;
			const firstQuarters = Math.ceil(totalMinutesInFirstPart / QUARTER_MINUTES);
			const firstWidthPx = Math.max(this.minutesToPx(firstPartMinutes), MIN_SCHEDULE_WIDTH);
			data[this.getColKey(startHour, startQuarter)] = this.createQuarterCell(firstWidthPx, firstQuarters, offsetPx, item);

			// remainder at beginning of day (hour 0, quarter 0)
			const secondPartMinutes = duration - firstPartMinutes;
			const secondQuarters = Math.ceil(secondPartMinutes / QUARTER_MINUTES);
			const secondWidthPx = Math.max(this.minutesToPx(secondPartMinutes), MIN_SCHEDULE_WIDTH);
			data[this.getColKey(0, 0)] = this.createQuarterCell(secondWidthPx, secondQuarters, 0, item);
		}
	}

	/**
	 * Creates a cell for a 15-minute quarter.
	 * @param width cell width in pixels
	 * @param quarters number of quarters this cell spans
	 * @param offset left offset in pixels
	 * @param props job schedule item properties
	 * @returns cell source object
	 */
	private createQuarterCell(widthPx: number, quarters: number = 1, offsetPx: number = 0, props?: JobSchedule): Partial<CellSource> {
		return {
			cs: quarters,
			t: 'h',
			v: `<div style="width: ${widthPx}px; height: 100%; background-color: #ff47cf; left: ${offsetPx}px; top: 0; position: absolute;"></div>`,
			comment: { text: '?' },
			props,
		};
	}

	// helper: parse time string "hh:mm" -> {h,m} | null
    private parseTime(time: string): { h: number; m: number } | null {
        if (!time) return null;
        const parts = time.split(':');
        if (parts.length !== 2) return null;
        const h = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        if (Number.isNaN(h) || Number.isNaN(m)) return null;
        if (h < 0 || h > 23 || m < 0 || m > 59) return null;
        return { h, m };
    }

    // helper: column key
    private getColKey(h: number, q: number) {
        return `col_${h}_${q}`;
    }

    // helper: minutes -> pixel width
    private minutesToPx(minutes: number) {
        return (minutes / QUARTER_MINUTES) * COL_WIDTH;
    }

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			rowNumbers: false,
			columnSorting: false,
			filters: false,
			pageSize: PAGE_SIZE,
			editable: false,
			lightTheme: null,
			darkTheme: null,
			freezeLeft: 1,
			toolbar: {
				save: {enabled: false},
				discard: {enabled: false},
				new: {enabled: false},
			},
			comments: {
				renderer: (div: HTMLDivElement, comment: CommentItem, cell: CellModel) => {
					dom.empty(div);
					div.style.padding = '0';
					div.append(dom.createElement<JobScheduleDetail>('rp-super-job-detail', { cell }));
					return div;
				},
			},
		});
	}

}
